// Based on https://github.com/bjoerge/debounce-promise
// Modified to avoid compilation errors.

import Promise from 'bluebird'

export function debouncePromise(fn, wait = 0, options: any = {}) {
  let lastCallAt: number | undefined
  let deferred
  let timer: ReturnType<typeof setTimeout> | undefined
  let pendingArgs: unknown[][] = []
  return function debounced(this: unknown, ...args: unknown[]) {
    const currentWait = getWait(wait)
    const currentTime = new Date().getTime()

    const isCold = !lastCallAt || (currentTime - lastCallAt) > currentWait

    lastCallAt = currentTime

    if (isCold && options.leading) {
      return options.accumulate
        ? Promise.resolve(fn.call(this, [args])).then((result: unknown[]) => result[0])
        : Promise.resolve(fn.call(this, ...args))
    }

    if (deferred) {
      clearTimeout(timer)
    } else {
      deferred = defer()
    }

    pendingArgs.push(args)
    timer = setTimeout(flush.bind(this), currentWait)

    if (options.accumulate) {
      const argsIndex = pendingArgs.length - 1
      return deferred.promise.then((results: unknown[]) => results[argsIndex])
    }

    return deferred.promise
  }

  function flush (this: unknown) {
    const thisDeferred = deferred
    clearTimeout(timer)

    Promise.resolve(
      options.accumulate
        ? fn.call(this, pendingArgs)
        : fn.apply(this, pendingArgs[pendingArgs.length - 1])
    )
      .then(thisDeferred.resolve, thisDeferred.reject)

    pendingArgs = []
    deferred = null
  }
}

function getWait(wait: number | (() => number)) {
  return (typeof wait === 'function') ? wait() : wait
}

function defer () {
  const deferred: any = {}
  deferred.promise = new Promise(
    (resolve: (value?: unknown) => void, reject: (reason?: unknown) => void) => {
      deferred.resolve = resolve
      deferred.reject = reject
    }
  )
  return deferred
}